import { useTranslation } from 'next-i18next';
import { useCallback, useState } from 'react';
import {
	getAuctionAccessPaymentMethods,
	getAuctionAccessSignupData,
	submitAuctionAccessDocumentsRequest,
	submitAuctionAccessReviewRequest,
} from './auctionAccessSignupApi';
import { trackAuctionAccessStart } from './Lookup/tracking';

export const AUCTION_ACCESS_SIGNUP_STAGE = {
	START: 'start',
	LOOKUP: 'lookup',
	REVIEW: 'review',
	DOCUMENTS: 'documents',
	SUBMITTING_DOCUMENTS: 'submitting_documents',
	PAYMENT_METHODS: 'payment_methods',
};

export const AUCTION_ACCESS_FRANCHISE_DEALER_TYPE = 'Franchise';

export const isVerifiedAuctionAccessSignup = (auctionAccess) =>
	auctionAccess?.decision === 'happy_path' || auctionAccess?.decision === 'company_selection_required';

const getInitialStage = ({ enabled, isOneSingleStep }) =>
	enabled && !isOneSingleStep ? AUCTION_ACCESS_SIGNUP_STAGE.START : null;

const getErrorMessage = (error, t) => error?.error?.message || error?.message || t('common:error');

export const requiresDriversLicenseUpload = (dealershipType) => dealershipType !== AUCTION_ACCESS_FRANCHISE_DEALER_TYPE;

const useAuctionAccessSignup = ({
	anonymousUser,
	dispatch,
	enabled = false,
	isOneSingleStep = false,
	onboardingLanguage,
	query = {},
	setSignUpEmail,
	signUpEmail = '',
	state,
	submitStep,
	treatments,
	updateStepURLQuery,
}) => {
	const { t } = useTranslation(['common']);
	const [stage, setStage] = useState(() => getInitialStage({ enabled, isOneSingleStep }));
	const [auctionAccessVerifiedAccount, setAuctionAccessVerifiedAccount] = useState();
	const [requiresDriverLicense, setRequiresDriverLicense] = useState(true);
	const [isReviewSubmitting, setIsReviewSubmitting] = useState(false);
	const [isPaymentMethodsRefreshing, setIsPaymentMethodsRefreshing] = useState(false);
	const [isPaymentMethodsSubmitting, setIsPaymentMethodsSubmitting] = useState(false);

	const isFeatureEnabled = Boolean(enabled && !isOneSingleStep);
	const dealershipType = state?.auctionAccessDealershipType;
	const paymentMethods = state?.values?.[3]?.paymentMethods;
	const isResumedAuctionAccessSignup =
		state?.resumeStep >= 0 && isVerifiedAuctionAccessSignup(state?.values?.[0]?.auctionAccess);
	const isResumedAuctionAccessDocuments =
		stage === AUCTION_ACCESS_SIGNUP_STAGE.START && state?.step === 2 && isResumedAuctionAccessSignup;
	const isResumedPaymentMethods =
		stage === AUCTION_ACCESS_SIGNUP_STAGE.START && state?.step === 3 && Boolean(paymentMethods?.status);
	const currentStage =
		isResumedPaymentMethods ? AUCTION_ACCESS_SIGNUP_STAGE.PAYMENT_METHODS
		: isResumedAuctionAccessDocuments ? AUCTION_ACCESS_SIGNUP_STAGE.DOCUMENTS
		: stage;
	const isLookupStep = isFeatureEnabled && state?.step === 0;
	const isLookupOpen =
		currentStage === AUCTION_ACCESS_SIGNUP_STAGE.LOOKUP || currentStage === AUCTION_ACCESS_SIGNUP_STAGE.REVIEW;
	const isReview = currentStage === AUCTION_ACCESS_SIGNUP_STAGE.REVIEW;
	const isDocuments = currentStage === AUCTION_ACCESS_SIGNUP_STAGE.DOCUMENTS;
	const isDocumentsSubmitting = currentStage === AUCTION_ACCESS_SIGNUP_STAGE.SUBMITTING_DOCUMENTS;
	const isPaymentMethods = currentStage === AUCTION_ACCESS_SIGNUP_STAGE.PAYMENT_METHODS;
	const isOnboardingFlow = isFeatureEnabled && (isLookupStep || currentStage !== AUCTION_ACCESS_SIGNUP_STAGE.START);
	const shouldRenderStandardSignup = !isFeatureEnabled || currentStage === AUCTION_ACCESS_SIGNUP_STAGE.START;

	const hydrateAuctionAccessSignup = useCallback(
		async (account) => {
			const partialSignUp = await getAuctionAccessSignupData();

			setSignUpEmail(partialSignUp.email);
			setAuctionAccessVerifiedAccount(account);
			dispatch({
				type: 'onHydrateAuctionAccessSignUp',
				token: partialSignUp.token,
				values: partialSignUp.stepsData,
			});
			setStage(AUCTION_ACCESS_SIGNUP_STAGE.REVIEW);
			updateStepURLQuery(0, { removeSignUpToken: true });
		},
		[dispatch, setSignUpEmail, updateStepURLQuery],
	);

	const startAuctionAccessLookup = useCallback(() => {
		trackAuctionAccessStart();
		setStage(AUCTION_ACCESS_SIGNUP_STAGE.LOOKUP);
	}, []);

	const continueWithoutAuctionAccess = useCallback(
		(initialValues) => {
			dispatch({ type: 'onRestoreStandardSignup', ...(initialValues && { values: initialValues }) });
			setSignUpEmail('');
			setAuctionAccessVerifiedAccount();
			setStage(AUCTION_ACCESS_SIGNUP_STAGE.START);
		},
		[dispatch, setSignUpEmail],
	);

	const openDocuments = useCallback(() => {
		setStage(AUCTION_ACCESS_SIGNUP_STAGE.DOCUMENTS);
	}, []);

	const completeDocuments = useCallback(
		async ({ token, values, email = signUpEmail }) => {
			try {
				const nextSignup = await submitAuctionAccessDocumentsRequest({
					anonymousUser,
					onboardingLanguage,
					query,
					signUpEmail: email,
					token,
					treatments,
					values,
				});

				if (!nextSignup.paymentMethods) {
					dispatch({ type: 'onSetError', error: t('common:error') });
					return false;
				}

				setSignUpEmail(nextSignup.signUpEmail);
				dispatch({ type: 'onSaveStepSuccess', token: nextSignup.token, step: 2, values });
				dispatch({ type: 'onSetAuctionAccessPaymentMethods', paymentMethods: nextSignup.paymentMethods });
				setStage(AUCTION_ACCESS_SIGNUP_STAGE.PAYMENT_METHODS);
				updateStepURLQuery(3);
				return true;
			} catch (error) {
				dispatch({ type: 'onSetError', error: getErrorMessage(error, t) });
				return false;
			}
		},
		[
			anonymousUser,
			dispatch,
			onboardingLanguage,
			query,
			setSignUpEmail,
			signUpEmail,
			t,
			treatments,
			updateStepURLQuery,
		],
	);

	const handleReviewNext = useCallback(
		async (values, dealershipType) => {
			if (isReviewSubmitting) return false;

			dispatch({ type: 'onSetError', error: '' });
			setIsReviewSubmitting(true);

			try {
				const reviewResponse = await submitAuctionAccessReviewRequest({
					marketplaceDocumentIds: values.marketplaceDocumentIds,
				});
				if (!reviewResponse?.token) {
					dispatch({ type: 'onSetError', error: t('common:error') });
					return false;
				}

				if (reviewResponse.signup_email) setSignUpEmail(reviewResponse.signup_email);
				dispatch({
					type: 'onSetAuctionAccessReviewValues',
					dealershipType,
					token: reviewResponse.token,
					values,
				});
				// Backend is fixing the logic to allow the driver license upload to be optional for franchise dealers. For now, we will always require it.
				const shouldRequestDriverLicense = true; //requiresDriversLicenseUpload(dealershipType);
				setRequiresDriverLicense(shouldRequestDriverLicense);

				if (shouldRequestDriverLicense) {
					openDocuments();
					return true;
				}

				setStage(AUCTION_ACCESS_SIGNUP_STAGE.SUBMITTING_DOCUMENTS);
				const documentsSubmitted = await completeDocuments({
					email: reviewResponse.signup_email || signUpEmail,
					token: reviewResponse.token,
					values: { ...(state?.values?.[2] || {}), ...values },
				});
				if (!documentsSubmitted) setStage(AUCTION_ACCESS_SIGNUP_STAGE.REVIEW);
				return documentsSubmitted;
			} catch (error) {
				dispatch({ type: 'onSetError', error: getErrorMessage(error, t) });
				return false;
			} finally {
				setIsReviewSubmitting(false);
			}
		},
		[completeDocuments, dispatch, isReviewSubmitting, openDocuments, setSignUpEmail, signUpEmail, state?.values, t],
	);

	const returnToAuctionAccessLookup = useCallback(() => {
		setStage(AUCTION_ACCESS_SIGNUP_STAGE.REVIEW);
		dispatch({ type: 'onReturnToAuctionAccessLookup' });
		updateStepURLQuery(0);
	}, [dispatch, updateStepURLQuery]);

	const handleLookupStateChange = useCallback((isReview) => {
		setStage((currentStage) => {
			if (isReview) return AUCTION_ACCESS_SIGNUP_STAGE.REVIEW;
			return currentStage === AUCTION_ACCESS_SIGNUP_STAGE.REVIEW ?
					AUCTION_ACCESS_SIGNUP_STAGE.LOOKUP
				:	currentStage;
		});
	}, []);

	const submitAuctionAccessDocuments = useCallback(
		async (values) => {
			if (stage === AUCTION_ACCESS_SIGNUP_STAGE.SUBMITTING_DOCUMENTS) return;

			const documentsValues = { ...(state?.values?.[2] || {}), ...values };

			dispatch({ type: 'onSetError', error: '' });
			setStage(AUCTION_ACCESS_SIGNUP_STAGE.SUBMITTING_DOCUMENTS);

			try {
				return await completeDocuments({ token: state.token, values: documentsValues });
			} finally {
				setStage((currentStage) =>
					currentStage === AUCTION_ACCESS_SIGNUP_STAGE.SUBMITTING_DOCUMENTS ?
						AUCTION_ACCESS_SIGNUP_STAGE.DOCUMENTS
					:	currentStage,
				);
			}
		},
		[completeDocuments, dispatch, stage, state.token, state.values],
	);

	const returnFromPaymentMethods = useCallback(() => {
		if (requiresDriverLicense) {
			openDocuments();
			return;
		}

		returnToAuctionAccessLookup();
	}, [openDocuments, requiresDriverLicense, returnToAuctionAccessLookup]);

	const refreshAuctionAccessPaymentMethods = useCallback(async () => {
		if (isPaymentMethodsRefreshing) return;

		dispatch({ type: 'onSetError', error: '' });
		setIsPaymentMethodsRefreshing(true);

		try {
			const refreshedPaymentMethods = await getAuctionAccessPaymentMethods(state.token);
			if (!refreshedPaymentMethods) throw new Error('Payment methods are unavailable');

			dispatch({ type: 'onSetAuctionAccessPaymentMethods', paymentMethods: refreshedPaymentMethods });
			return true;
		} catch (error) {
			dispatch({ type: 'onSetError', error: getErrorMessage(error, t) });
			return false;
		} finally {
			setIsPaymentMethodsRefreshing(false);
		}
	}, [dispatch, isPaymentMethodsRefreshing, state.token, t]);

	const submitAuctionAccessPaymentMethods = useCallback(
		async (values) => {
			if (isPaymentMethodsSubmitting) return;

			dispatch({ type: 'onSetError', error: '' });
			setIsPaymentMethodsSubmitting(true);

			try {
				return await submitStep(3, values, undefined, { isAuctionAccess: true });
			} finally {
				setIsPaymentMethodsSubmitting(false);
			}
		},
		[dispatch, isPaymentMethodsSubmitting, submitStep],
	);

	const updatePaymentMethodSelection = useCallback(
		(selection) => dispatch({ type: 'onSetAuctionAccessPaymentMethodSelection', selection }),
		[dispatch],
	);

	return {
		auctionAccessVerifiedAccount,
		continueWithoutAuctionAccess,
		dealershipType,
		error: state?.error,
		handleLookupStateChange,
		hydrateAuctionAccessSignup,
		isDocumentsSubmitting,
		isFeatureEnabled,
		isLookupOpen,
		isDocuments,
		isOnboardingFlow,
		isPaymentMethods,
		isReview,
		isReviewSubmitting,
		isResumedAuctionAccessSignup,
		isSubmitting: isPaymentMethodsSubmitting || isPaymentMethodsRefreshing,
		handleReviewNext,
		openDocuments,
		paymentMethods,
		refreshAuctionAccessPaymentMethods,
		returnFromPaymentMethods,
		returnToAuctionAccessLookup,
		stage: currentStage,
		startAuctionAccessLookup,
		shouldRenderStandardSignup,
		submitAuctionAccessDocuments,
		submitAuctionAccessPaymentMethods,
		updatePaymentMethodSelection,
	};
};

export default useAuctionAccessSignup;
